import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { X, Upload, Loader2, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

// Helper to resolve image URLs
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return url.startsWith('/') ? `${BACKEND_URL}${url}` : `${BACKEND_URL}/${url}`;
};

const EditArtworkModal = ({ artwork, onClose, onUpdate }) => {
  const [title, setTitle] = useState(artwork?.title || '');
  const [description, setDescription] = useState(artwork?.description || '');
  const [tagsInput, setTagsInput] = useState((artwork?.tags || []).join(', '));
  const [imageUrl, setImageUrl] = useState(artwork?.image_url || artwork?.images?.[0]?.url || '');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageKey, setImageKey] = useState(Date.now()); // For cache busting
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Set initial preview URL with cache busting
    const url = artwork?.image_url || artwork?.images?.[0]?.url;
    if (url) {
      setPreviewUrl(`${resolveImageUrl(url)}?v=${Date.now()}`);
    }
  }, [artwork]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Show preview immediately
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);

    // Upload to Cloudinary
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload/artwork`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const newImageUrl = response.data.url;
      setImageUrl(newImageUrl);
      setPreviewUrl(`${resolveImageUrl(newImageUrl)}?v=${Date.now()}`);
      setImageKey(Date.now());
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
      // Revert preview to original
      const originalUrl = artwork?.image_url || artwork?.images?.[0]?.url;
      if (originalUrl) {
        setPreviewUrl(`${resolveImageUrl(originalUrl)}?v=${Date.now()}`);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Parse tags from comma-separated input
      const tags = tagsInput
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const updatePayload = {
        title: title.trim() || null, // Allow empty title (will display as "Untitled")
        description: description.trim(),
        tags,
      };

      // Only include image_url if it changed
      const originalImageUrl = artwork?.image_url || artwork?.images?.[0]?.url;
      if (imageUrl && imageUrl !== originalImageUrl) {
        updatePayload.image_url = imageUrl;
      }

      const response = await axios.put(`${API}/art/post/${artwork.id}`, updatePayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Artwork updated successfully');
      
      // Pass updated artwork back to parent with cache-busting timestamp
      if (onUpdate) {
        onUpdate({
          ...response.data,
          _updated_at: Date.now() // For cache busting in parent
        });
      }
      onClose();
    } catch (error) {
      console.error('Error updating artwork:', error);
      if (error.response?.status === 403) {
        toast.error('You are not authorized to edit this artwork');
      } else {
        toast.error('Failed to update artwork');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="edit-artwork-modal">
      <Card className="bg-zinc-900 border-zinc-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between border-b border-zinc-800 pb-4">
          <CardTitle className="text-xl text-white">Edit Artwork</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-zinc-400 hover:text-white"
            data-testid="edit-artwork-close"
          >
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          {/* Image Preview & Upload */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">Image</label>
            <div 
              className="relative aspect-video bg-zinc-800 rounded-lg overflow-hidden cursor-pointer border-2 border-dashed border-zinc-600 hover:border-pink-500 transition-colors"
              onClick={() => !uploading && fileInputRef.current?.click()}
            >
              {previewUrl ? (
                <img 
                  key={imageKey}
                  src={previewUrl} 
                  alt="Preview" 
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500">
                  <ImageIcon className="w-12 h-12 mb-2" />
                  <p>Click to upload image</p>
                </div>
              )}
              
              {uploading && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
                </div>
              )}
              
              {!uploading && previewUrl && (
                <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="flex items-center gap-2 text-white bg-black/60 px-4 py-2 rounded-lg">
                    <Upload className="w-5 h-5" />
                    <span>Replace Image</span>
                  </div>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="sr-only"
              data-testid="edit-artwork-image-input"
            />
            <p className="text-xs text-zinc-500 mt-2">Click on the image to replace it</p>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">Title</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter artwork title (optional)"
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="edit-artwork-title"
            />
            <p className="text-xs text-zinc-500 mt-1">Leave empty to display as &quot;Untitled&quot;</p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your artwork..."
              rows={4}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-pink-500 resize-none"
              data-testid="edit-artwork-description"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-2">Tags</label>
            <Input
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="digital art, illustration, fantasy (comma-separated)"
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="edit-artwork-tags"
            />
            <p className="text-xs text-zinc-500 mt-1">Separate tags with commas</p>
            
            {/* Tag Preview */}
            {tagsInput && (
              <div className="flex flex-wrap gap-2 mt-3">
                {tagsInput.split(',').map((tag, i) => {
                  const trimmed = tag.trim();
                  if (!trimmed) return null;
                  return (
                    <Badge key={i} variant="secondary" className="bg-zinc-800 text-zinc-300">
                      {trimmed}
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-zinc-800">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              disabled={saving || uploading}
              data-testid="edit-artwork-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
              disabled={saving || uploading}
              data-testid="edit-artwork-save"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditArtworkModal;
